/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Commission payouts — GET /re/commission-payouts, GET /re/commission-payouts/{id} (entries),
 * POST /re/commission-payouts/{id}/mark-paid {paid_date, payment_reference, broker_invoice_no?}.
 * `?open=<id>` opens that payout's drawer (used after "Create payout" in the ledger).
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer, Icon, IconButton, Skeleton, TextField,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { usePermission } from 'auth/usePermission';
import RePage, { FilterBar, LoadError, RePanel, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReTable from './components/ReTable';
import { RePicker, VocabSelect } from './components/Pickers';
import { DateField, KV, Num } from './components/Widgets';
import {
  PAYOUT_STATUSES, errorMessage, formatDate, formatMoney, humanize, todayIso, useReApi, useReGet, useReList,
} from './api';
import { drawerPaperSx } from './components/drawerPaper';

function MarkPaidDialog({ payout, open, onClose, onDone }) {
  const { api } = useReApi();
  const [form, setForm] = useState({ paid_date: todayIso(), payment_reference: '', broker_invoice_no: '' });
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({ paid_date: todayIso(), payment_reference: '', broker_invoice_no: payout?.broker_invoice_no || '' });
      setErr(null);
    }
  }, [open, payout]);
  const submit = async () => {
    if (!form.payment_reference.trim()) {
      setErr('Enter the payment reference (transfer or cheque number).');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/re/commission-payouts/${payout.id}/mark-paid`, {
        paid_date: form.paid_date, payment_reference: form.payment_reference.trim(), broker_invoice_no: form.broker_invoice_no || null,
      });
      onDone();
    } catch (e) {
      setErr(errorMessage(e, 'Could not mark the payout paid'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{`Mark ${payout?.payout_number || 'payout'} paid`}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <MDBox mb={2}><KV label="Amount" value={formatMoney(payout?.total_amount, payout?.currency)} bold /></MDBox>
        <MDBox display="flex" flexDirection="column" gap={2}>
          <DateField label="Paid on" value={form.paid_date} onChange={(v) => setForm({ ...form, paid_date: v })} />
          <TextField size="small" label="Payment reference" value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} />
          <TextField size="small" label="Broker tax invoice no." value={form.broker_invoice_no} onChange={(e) => setForm({ ...form, broker_invoice_no: e.target.value })} />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="success" onClick={submit} disabled={saving}>Mark paid</MDButton>
      </DialogActions>
    </Dialog>
  );
}

function PayoutDrawer({ id, onClose, onChanged, notify }) {
  const { canEdit } = usePermission('re_commission_entries');
  const { data: p, loading, error, reload } = useReGet(id ? `/re/commission-payouts/${id}` : null);
  const [payOpen, setPayOpen] = useState(false);
  return (
    <Drawer anchor="right" open={Boolean(id)} onClose={onClose} PaperProps={{ sx: drawerPaperSx({ xs: '100%', md: 640 }) }}>
      <MDBox p={2.5}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <MDTypography variant="h5" fontWeight="bold">{p?.payout_number || 'Payout'}</MDTypography>
          <IconButton onClick={onClose}><Icon>close</Icon></IconButton>
        </MDBox>
        <LoadError error={error} onRetry={reload} />
        {loading && !p && <Skeleton variant="rectangular" height={200} />}
        {p && (
          <>
            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
              <ReStatusChip vocab="payout" status={p.status} />
              <MDTypography variant="button" color="text">{p.payee_name || humanize(p.payee_type)}</MDTypography>
            </MDBox>
            <KV label="Gross" value={formatMoney(p.gross_amount, p.currency)} />
            <KV label="VAT" value={formatMoney(p.vat_amount, p.currency)} />
            <KV label="Total" value={formatMoney(p.total_amount, p.currency)} bold />
            {p.status === 'PAID' && (
              <>
                <KV label="Paid on" value={formatDate(p.paid_date)} />
                <KV label="Reference" value={p.payment_reference || '—'} />
              </>
            )}
            <KV label="Broker invoice no." value={p.broker_invoice_no || '—'} />
            <Divider sx={{ my: 2 }} />
            <MDTypography variant="button" fontWeight="bold">Entries</MDTypography>
            <ReTable
              dense
              rows={p.entries || []}
              emptyMessage="No entries."
              columns={[
                { key: 'booking_number', label: 'Deal', render: (c) => c.booking_number || c.lease_number || '—' },
                { key: 'project_name', label: 'Project', render: (c) => [c.project_name, c.unit_number].filter(Boolean).join(' · ') || '—' },
                { key: 'gross_amount', label: 'Gross', align: 'right', render: (c) => <Num>{formatMoney(c.gross_amount, c.currency)}</Num> },
                { key: 'total_amount', label: 'Total', align: 'right', render: (c) => <Num bold>{formatMoney(c.total_amount, c.currency)}</Num> },
              ]}
            />
            {canEdit && ['DRAFT', 'APPROVED'].includes(p.status) && (
              <MDBox mt={2}>
                <MDButton variant="gradient" color="success" onClick={() => setPayOpen(true)}><Icon>check</Icon>&nbsp;Mark paid</MDButton>
              </MDBox>
            )}
            <MarkPaidDialog
              payout={p}
              open={payOpen}
              onClose={() => setPayOpen(false)}
              onDone={() => { setPayOpen(false); notify('Payout marked paid'); reload(); onChanged(); }}
            />
          </>
        )}
      </MDBox>
    </Drawer>
  );
}

export default function CommissionPayouts() {
  const [params, setParams] = useSearchParams();
  const [snack, notify] = useSnack();
  const [status, setStatus] = useState(null);
  const [firmId, setFirmId] = useState(null);
  const openId = params.get('open') ? Number(params.get('open')) : null;
  const list = useReList('/re/commission-payouts', { status, broker_firm_id: firmId, sort_by: 'created_at', sort_order: 'desc' });

  const setOpen = (id) => setParams(id ? { open: String(id) } : {});

  return (
    <RePage title="Commission payouts" subtitle="Batches of approved commission paid to a broker, agent or staff member">
      <FilterBar>
        <VocabSelect label="Status" value={status} onChange={setStatus} values={PAYOUT_STATUSES} />
        <RePicker path="/re/broker-firms" label="Broker firm" value={firmId} onChange={setFirmId} />
      </FilterBar>
      <LoadError error={list.error} onRetry={list.reload} />
      <RePanel p={0}>
        <ReTable
          loading={list.loading}
          rows={list.items}
          emptyMessage="No payouts yet. Create one from approved entries in the commission ledger."
          onRowClick={(p) => setOpen(p.id)}
          total={list.total}
          page={list.page}
          pageSize={list.pageSize}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          columns={[
            { key: 'payout_number', label: 'Payout', render: (p) => <strong>{p.payout_number || `#${p.id}`}</strong> },
            { key: 'payee_name', label: 'Payee', render: (p) => p.payee_name || p.broker_firm_name || humanize(p.payee_type) },
            { key: 'created_at', label: 'Created', render: (p) => formatDate(p.created_at) },
            { key: 'gross_amount', label: 'Gross', align: 'right', render: (p) => <Num>{formatMoney(p.gross_amount, p.currency)}</Num> },
            { key: 'vat_amount', label: 'VAT', align: 'right', render: (p) => <Num>{formatMoney(p.vat_amount, p.currency)}</Num> },
            { key: 'total_amount', label: 'Total', align: 'right', render: (p) => <Num bold>{formatMoney(p.total_amount, p.currency)}</Num> },
            { key: 'paid_date', label: 'Paid', render: (p) => formatDate(p.paid_date) },
            { key: 'status', label: 'Status', render: (p) => <ReStatusChip vocab="payout" status={p.status} size="xs" /> },
          ]}
        />
      </RePanel>
      <PayoutDrawer id={openId} onClose={() => setOpen(null)} onChanged={list.reload} notify={notify} />
      {snack}
    </RePage>
  );
}
